/**
 * Visual billing status — derived from billing_day + last_payment_date.
 *
 * IMPORTANT: this is NOT the access gate. The coach hasn't approved
 * auto-blocking yet, so `payment_status` on coach_clients remains
 * 'paid' for everyone unless the coach manually flips it. This helper
 * is purely for the cobranza UI.
 *
 * Statuses:
 *   - no_config: coach hasn't set monthly_amount or billing_day yet
 *   - paid:      paid this cycle, days_until_next_due > 7
 *   - due_soon:  next billing day is within 7 days
 *   - due_today: today is the billing day
 *   - overdue:   billing day passed without payment
 */

export type BillingStatus =
  | 'no_config'
  | 'paid'
  | 'due_soon'
  | 'due_today'
  | 'overdue';

export interface BillingComputed {
  status: BillingStatus;
  /** Next billing day as a Date (or null when no_config). */
  nextDueDate: Date | null;
  /** Days until (positive) or since (negative) the next billing day. */
  daysToNextDue: number | null;
  /** Whether the client has paid in the current cycle. */
  paidThisCycle: boolean;
}

/**
 * Compute billing status. Pure function — no I/O.
 *
 * @param billingDay        day of the month (1–31), e.g. 5
 * @param lastPaymentDate   ISO date string (YYYY-MM-DD) or null
 * @param now               injectable for tests; defaults to current local date
 */
export function computeBillingStatus(
  billingDay: number | null | undefined,
  lastPaymentDate: string | null | undefined,
  now: Date = new Date(),
): BillingComputed {
  if (!billingDay) {
    return { status: 'no_config', nextDueDate: null, daysToNextDue: null, paidThisCycle: false };
  }

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = Math.min(31, Math.max(1, billingDay));

  // Compute "this cycle's billing day" — the most recent occurrence of
  // billingDay that is <= today. If today is < billingDay this month,
  // that's last month's.
  const thisCycleStart = (() => {
    const candidate = new Date(today.getFullYear(), today.getMonth(), day);
    if (candidate <= today) return candidate;
    // Previous month's billing day
    return new Date(today.getFullYear(), today.getMonth() - 1, day);
  })();

  // Next billing day from today (>= today). If today === billingDay
  // we treat it as due_today.
  const nextDue = (() => {
    const candidate = new Date(today.getFullYear(), today.getMonth(), day);
    if (candidate >= today) return candidate;
    return new Date(today.getFullYear(), today.getMonth() + 1, day);
  })();

  const daysToNextDue = Math.round(
    (nextDue.getTime() - today.getTime()) / 86400000,
  );

  // Did the client pay during the current cycle? (i.e. on or after
  // thisCycleStart)
  const paidThisCycle = !!lastPaymentDate
    && new Date(lastPaymentDate + 'T12:00:00') >= thisCycleStart;

  let status: BillingStatus;
  if (paidThisCycle) {
    status = daysToNextDue <= 7 ? 'due_soon' : 'paid';
  } else {
    // No payment this cycle.
    if (daysToNextDue === 0) status = 'due_today';
    else if (daysToNextDue > 0 && daysToNextDue <= 7) status = 'due_soon';
    else status = 'overdue';
  }

  return { status, nextDueDate: nextDue, daysToNextDue, paidThisCycle };
}

/** Spanish label per status, used in badges and tables. */
export function billingStatusLabel(s: BillingStatus): string {
  switch (s) {
    case 'no_config': return 'Sin configurar';
    case 'paid':      return 'Al día';
    case 'due_soon':  return 'Vence pronto';
    case 'due_today': return 'Vence hoy';
    case 'overdue':   return 'Atrasado';
  }
}

/** Tailwind class chunk for badge colour per status. */
export function billingStatusClasses(s: BillingStatus): string {
  switch (s) {
    case 'no_config': return 'bg-secondary text-muted-foreground border-border';
    case 'paid':      return 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30';
    case 'due_soon':  return 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30';
    case 'due_today': return 'bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30';
    case 'overdue':   return 'bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30';
  }
}

/** Human-friendly money formatting in MXN. */
export function formatMoney(amount: number | null | undefined): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN', maximumFractionDigits: 0,
  }).format(Number(amount));
}
